import httpStatus from 'statuses'
import i18n from '@plugins/vue-i18n'
import ErrorPageCodes from '@/shared/constants/error/ErrorPageCodes'

// Abstract Product
const BaseErrorPage = (errorCode) => {
  let errorStatusText = ''
  try {
    errorStatusText = httpStatus(errorCode)
  } catch (error) {
    errorStatusText = 'Unknown'
  }

  return {
    // Properties
    showErrorCode: true,
    errorCode,
    errorText: `${errorCode} ${errorStatusText}`,
    title: i18n.t('error.errorGenericTitle'),
    desc: i18n.t('error.errorGenericDescription'),
    subDesc: i18n.t('error.errorGenericSubDescription'),
    goBackURL: '/',
    goBackText: i18n.t('error.errorGenericReturnButtonText'),
  }
}

// Factory
export function ErrorPageFactory(errorCode) {
  switch (errorCode) {
    case ErrorPageCodes.INTERNAL_SERVER_ERROR.id:
      return InternalServerError()
    case ErrorPageCodes.PAGE_NOT_FOUND.id:
      return PageNotFoundError()
    case ErrorPageCodes.UNAUTHORIZED.id:
      return UnauthorizedError()
    case ErrorPageCodes.FORBIDDEN.id:
      return ForbiddenError()
    case ErrorPageCodes.BAD_REQUEST.id:
      return BadRequestError()
    case ErrorPageCodes.ACTION_LOCKED.id:
      return ActionLockedError()
    case ErrorPageCodes.ACCOUNT_LOAD_FAILURE.id:
      return AccountLoadFailedError()
    case ErrorPageCodes.EXPIRED.id:
      return ExpiredError()
    case ErrorPageCodes.NO_SERVER_RESPONSE.id:
      return NoServerResponseError()
    default:
      return BaseErrorPage(ErrorPageCodes.INTERNAL_SERVER_ERROR.statusCode)
  }
}

// Concrete Products
const InternalServerError = () => {
  return BaseErrorPage(ErrorPageCodes.INTERNAL_SERVER_ERROR.statusCode)
}

const BadRequestError = () => {
  const base = BaseErrorPage(ErrorPageCodes.BAD_REQUEST.statusCode)
  base.desc = i18n.t('error.badRequestDesc')
  base.subDesc = i18n.t('error.badRequestSubDesc')
  base.showErrorCode = true
  return base
}

const PageNotFoundError = () => {
  const base = BaseErrorPage(ErrorPageCodes.PAGE_NOT_FOUND.statusCode)
  base.title = i18n.t('error.errorNotFoundTitle')
  base.desc = i18n.t('error.errorNotFoundDescription')
  base.subDesc = i18n.t('error.errorNotFoundSubDescription')
  base.showErrorCode = true
  return base
}

const UnauthorizedError = () => {
  const base = BaseErrorPage(ErrorPageCodes.UNAUTHORIZED.statusCode)
  base.desc = i18n.t('error.errorUnauthorizedDescription')
  base.subDesc = i18n.t('error.errorUnauthorizedSubDescription', {
    companyName: i18n.t('app.companyName'),
  })
  base.showErrorCode = true
  return base
}

const ForbiddenError = () => {
  const base = BaseErrorPage(ErrorPageCodes.FORBIDDEN.statusCode)
  base.desc = i18n.t('error.errorUnauthorizedDescription')
  base.subDesc = i18n.t('error.errorUnauthorizedSubDescription', {
    companyName: i18n.t('app.companyName'),
  })
  base.showErrorCode = true
  return base
}

const ActionLockedError = () => {
  const base = BaseErrorPage(ErrorPageCodes.ACTION_LOCKED.statusCode)

  base.desc = i18n.t('error.errorActionLockedDescription')
  base.subDesc = i18n.t('error.errorActionLockedSubDescription')
  base.showErrorCode = false
  return base
}

const ExpiredError = () => {
  const base = BaseErrorPage(ErrorPageCodes.EXPIRED.statusCode)
  base.title = i18n.t('error.errorExpiredTitle')
  base.desc = i18n.t('error.errorExpiredDescription')
  base.subDesc = ''
  base.showErrorCode = false
  return base
}

const AccountLoadFailedError = () => {
  const base = BaseErrorPage(ErrorPageCodes.ACCOUNT_LOAD_FAILURE.statusCode)
  base.desc = i18n.t('error.accountLoadFailureDesc')
  base.subDesc = i18n.t('error.accountLoadFailureSubDesc', {
    companyName: i18n.t('app.companyName'),
  })
  base.showErrorCode = false
  base.errorText = i18n.t('error.accountLoadFailureDesc')
  return base
}

const NoServerResponseError = () => {
  const base = BaseErrorPage(ErrorPageCodes.NO_SERVER_RESPONSE.statusCode)
  base.desc = i18n.t('error.noServerResponseDesc')
  base.subDesc = i18n.t('error.noServerResponseSubDesc', {
    companyName: i18n.t('app.companyName'),
  })
  base.showErrorCode = false
  base.errorText = i18n.t('error.noServerResponseDesc')
  return base
}
